/**
 * Keyboard channel navigation helpers for the sidebar.
 *
 * The sidebar attribute `data-sidebar-channel-id` is added to every
 * focusable channel row (regular channels, space child channels,
 * 1-on-1 DMs, group DMs). DOM order = visible order, which means
 * `readSidebarChannelOrder` naturally handles collapsed sections,
 * filters, and per-space expand state — anything not in the DOM
 * isn't in the rotation.
 *
 * `findAdjacentChannelId` is pure so it's testable without jsdom-mounting
 * a sidebar.
 */

export const SIDEBAR_CHANNEL_ID_ATTR = "data-sidebar-channel-id";

export function readSidebarChannelOrder(
  container: ParentNode = document,
): string[] {
  const els = container.querySelectorAll<HTMLElement>(
    `[${SIDEBAR_CHANNEL_ID_ATTR}]`,
  );
  const ids: string[] = [];
  for (const el of els) {
    const id = el.dataset.sidebarChannelId;
    if (id) ids.push(id);
  }
  return ids;
}

/**
 * Find the channel id adjacent to `selected` in the given order.
 *
 * - No selection → falls onto the first (next) or last (prev) item.
 * - At the boundary (top with prev, bottom with next) → returns `null`
 *   so the caller can treat it as a no-op rather than wrap.
 * - Selection not in the list → falls onto the first/last as if no
 *   selection (handles the "I switched workspaces" case gracefully).
 */
export function findAdjacentChannelId(
  ids: readonly string[],
  selected: string | null,
  direction: -1 | 1,
): string | null {
  if (ids.length === 0) return null;
  const idx = selected ? ids.indexOf(selected) : -1;
  if (idx === -1) {
    return direction === 1 ? ids[0] : ids[ids.length - 1];
  }
  const next = idx + direction;
  if (next < 0 || next >= ids.length) return null;
  return ids[next];
}
